import React, { useState, useMemo } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
// mui components
import { Typography, Grid } from '@mui/material';
// components
import { Badge } from '../components/badge';
// custom hooks
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { useSidePanel } from 'hooks/useSidePanel';
// api
import {
  useCreateNotificationViewsBulkMutation,
  useGetUnviewedNotificationsQuery,
} from 'redux-store/api/activity-feed';
import { useGetProjectsQuery } from 'redux-store/api/projects';
// icons
import NotificationsBell from 'assets/icons/Bell_new_icon.svg';
// utils
import { getTimeSpend } from 'utils/helpers';
// selectors
import { queryArgsSelector } from 'redux-store/selector';
// styles
import { styles } from './styles';
import { NotificationMessage } from './notification-message';

interface INotificationButtonProps {
  projectId: number;
  unviewedItemsCount?: number;
  handleSeeMore?: () => void;
}

export const NotificationButton = ({
  projectId,
  unviewedItemsCount,
  handleSeeMore,
}: INotificationButtonProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleSidePanelOpen, handleSidePanelContent } = useSidePanel();
  const { projects: projectsArgs } = useAppSelector(queryArgsSelector);

  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const notificationButtonId = open ? 'notification-button-popover' : undefined;

  const [bulkUpdate] = useCreateNotificationViewsBulkMutation();

  const { isLoading, data } = useGetUnviewedNotificationsQuery(
    { limit: 15, projectId, cursor },
    { skip: !open }
  );
  const { data: projectsData } = useGetProjectsQuery(projectsArgs);

  const unviewedCount = useMemo(
    () => (typeof unviewedItemsCount === 'number' ? unviewedItemsCount : 0),
    [unviewedItemsCount]
  );

  const markNotificationsAsViewed = () => {
    const unviewedCountArgs =
      location.pathname.includes('account-dashboard') && projectsData?.items?.length
        ? projectsData.items.map(({ id }) => id)
        : undefined;

    bulkUpdate({ projectId: Number(projectId), unviewedCountArgs });
  };

  const getMore = () => {
    if (data?.meta?.nextCursor) {
      setCursor(data?.meta?.nextCursor);
    }
  };

  const onOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    if (unviewedCount > 0) {
      markNotificationsAsViewed();
    }
    setCursor(undefined);
    setAnchorEl(null);
  };

  const onSeeMore = () => {
    onClose();

    if (handleSeeMore) {
      handleSeeMore();
    } else {
      handleSidePanelContent('notifications_feed');
      handleSidePanelOpen(true);
    }
  };

  const onItemClick = (id: number) => {
    onClose();

    const isProjectDashboard = Boolean(location.pathname.split('/')?.[2]);

    if (!isProjectDashboard) {
      navigate(`/project-dashboard/${projectId}?#${id}`);
      return;
    }

    document.getElementById(id.toString())?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const hasData = data?.items?.length > 0;
  const isNoContentVisible = !hasData && open && !isLoading;
  const isLoadingVisible = !hasData && open && isLoading;
  const isContentVisible = hasData && open;

  return (
    <Badge
      open={open}
      id={notificationButtonId}
      popoverProps={{ anchorEl }}
      buttonProps={{ title: `${unviewedCount}`, icon: <NotificationsBell /> }}
      onClose={onClose}
      onOpen={onOpen}
    >
      <Badge.Header heading="Recent Notifications" actionText="See all" onActionClick={onSeeMore} />
      <Badge.Container additionalSx={styles.badgeContainer}>
        {isNoContentVisible && (
          <Badge.NoContent
            heading="No Notifications"
            subheading="Looks like you haven’t received any notifications"
          />
        )}
        {isLoadingVisible && <Badge.Loading />}
        {isContentVisible && (
          <Badge.InfiniteScroll
            getMore={getMore}
            hasMore={!!data?.meta && !!data?.meta?.nextCursor}
            dataLength={data?.items?.length}
          >
            {data?.items?.map((post) => {
              return (
                <Grid sx={styles.paperItem} key={post.id}>
                  <Grid sx={styles.paperTextContainer}>
                    <Grid sx={styles.greenDotContainer}>
                      {!post.isViewed ? <Grid sx={styles.greenDot}></Grid> : null}
                    </Grid>
                    <NotificationMessage post={post} onItemClick={onItemClick} />
                  </Grid>
                  <Grid sx={styles.dateContainer}>
                    <Typography sx={styles.dateText}>
                      {getTimeSpend(formatDistanceToNowStrict(new Date(post.createdAt)))}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Badge.InfiniteScroll>
        )}
      </Badge.Container>
    </Badge>
  );
};
